import axios from "axios";
import { extractErrorMessage } from "../../utils/errorUtils";

const SYSTEM_SIGNOUT_MESSAGE =
  "Signed out: session expired or active elsewhere. Log in to continue.";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_GATEWAY_URL,
  headers: {
    "Content-Type": "text/plain",
  },
  withCredentials: true,
});

// Store user email in closure instead of localStorage
let currentUserEmail: string | null = null;

export const setAxiosUserEmail = (email: string | null) => {
  currentUserEmail = email;
};

// Prevent multiple modals from opening
let isHandlingAuthError = false;

// Reset lock after user clicks "Log in"
window.addEventListener("authErrorHandled", () => {
  isHandlingAuthError = false;
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers["X-User-Email"] = currentUserEmail ?? "";
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // 1) Ignore cancellations (common with React Query / route changes)
    if (axios.isCancel?.(error) || error?.code === "ERR_CANCELED") {
      return Promise.reject(error);
    }

    const status: number | undefined = error?.response?.status;

    const hasUserEmailHeaderContext =
      typeof currentUserEmail === "string" && currentUserEmail.length > 0;

    // 2) If there's NO response, it's usually network/CORS/DNS/etc.
    // TEMPORARY FIX: on some portal domains a real 401 gets hidden as a plain
    // network error (missing CORS header upstream), so we treat network
    // errors while logged in as a 401 redirect, to keep logout/redirect consistent.
    // This also logs out the user and clear their session.
    if (!status) {
      if (hasUserEmailHeaderContext) {
        window.dispatchEvent(new CustomEvent("clearUser"));

        if (window.location.pathname !== "/login") {
          window.dispatchEvent(new CustomEvent("redirectToLogin"));
        }

        return Promise.reject(error);
      }

      const message =
        typeof error?.message === "string" && error.message.length
          ? error.message
          : "Network error. Please check your connection and try again.";

      window.dispatchEvent(
        new CustomEvent("showToast", {
          detail: { message, type: "error", duration: 2500 },
        }),
      );

      return Promise.reject(error);
    }

    // 3) 403 -> show system sign out modal
    if (status === 403) {
      if (!isHandlingAuthError) {
        isHandlingAuthError = true;

        window.dispatchEvent(
          new CustomEvent("systemSignOut", {
            detail: { message: SYSTEM_SIGNOUT_MESSAGE },
          }),
        );
      }
      return Promise.reject(error);
    }

    // 4) 401 handling:
    // Only force-redirect to login when we were operating in an authenticated context.
    // Invitation / login / password reset flows may legitimately hit protected endpoints
    // before X-User-Email is available; redirecting there breaks the flow.
    if (status === 401) {
      if (hasUserEmailHeaderContext) {
        window.dispatchEvent(new CustomEvent("clearUser"));

        if (window.location.pathname !== "/login") {
          window.dispatchEvent(new CustomEvent("redirectToLogin"));
        }
      }

      return Promise.reject(error);
    }

    // 5) 423 -> assignment locked (max attempts exceeded). Let the SCORM
    // player show a blocking overlay.
    if (status === 423) {
      const message = extractErrorMessage(error, "");

      window.dispatchEvent(
        new CustomEvent("assignmentAttemptsExceeded", {
          detail: { message },
        }),
      );

      return Promise.reject(error);
    }

    // For 4001 and 404, redirect to error page with message
    // if (status === 4001 || status === 404) {
    //   // prevent navigation loop if already on error page
    //   if (window.location.pathname.startsWith("/error/")) {
    //     return Promise.reject(error);
    //   }

    //   const msg = extractErrorMessage(
    //     error,
    //     status === 4001
    //       ? "Access denied. Unauthorized."
    //       : "The requested resource was not found.",
    //   );

    //   window.dispatchEvent(
    //     new CustomEvent("navigateToErrorPage", {
    //       detail: { status, message: msg },
    //     }),
    //   );

    //   return Promise.reject(error);
    // }

    // 6) everything else show mapped toast
    const message = extractErrorMessage(
      error,
      "Something went wrong. Please try again.",
    );

    window.dispatchEvent(
      new CustomEvent("showToast", {
        detail: { message, type: "error", duration: 2500 },
      }),
    );

    return Promise.reject(error);
  },
);

export default axiosInstance;
